import * as RadixMenu from '@radix-ui/react-dropdown-menu';
import { Check, MoreHorizontal } from 'lucide-react';
import type { ReactNode } from 'react';
import { useT } from '../../i18n';
import { cn } from '../../lib/cn';

export const Menu = ({
  trigger,
  children,
  align = 'end',
}: {
  trigger?: ReactNode;
  children: ReactNode;
  /**
   * Which edge of the menu meets the trigger. The default suits a row's
   * trailing "…" button; a small trigger in the middle of a card — a chip —
   * passes 'start', so the menu opens under it instead of reaching left.
   */
  align?: 'start' | 'end';
}) => {
  const { t } = useT();

  return (
  <RadixMenu.Root>
    <RadixMenu.Trigger asChild>
      {trigger ?? (
        <button
          type="button"
          aria-label={t('common.moreActions')}
          className="inline-flex size-8 items-center justify-center rounded-md text-ink-subtle transition-colors hover:bg-sunken hover:text-ink"
        >
          <MoreHorizontal className="size-4" />
        </button>
      )}
    </RadixMenu.Trigger>
    <RadixMenu.Portal>
      <RadixMenu.Content
        align={align}
        sideOffset={4}
        collisionPadding={8}
        className="z-50 min-w-44 rounded-lg border border-border bg-surface p-1 shadow-lg"
      >
        {children}
      </RadixMenu.Content>
    </RadixMenu.Portal>
  </RadixMenu.Root>
  );
};

export const MenuItem = ({
  children,
  onSelect,
  destructive = false,
  disabled = false,
}: {
  children: ReactNode;
  onSelect: () => void;
  destructive?: boolean;
  disabled?: boolean;
}) => (
  <RadixMenu.Item
    disabled={disabled}
    onSelect={onSelect}
    className={cn(
      'flex cursor-pointer items-center gap-2 rounded-md px-2 py-1.5 text-[13px] outline-none',
      'data-[highlighted]:bg-sunken data-[disabled]:cursor-not-allowed data-[disabled]:opacity-50',
      destructive ? 'text-fail' : 'text-ink',
    )}
  >
    {children}
  </RadixMenu.Item>
);

export const MenuSeparator = () => <RadixMenu.Separator className="my-1 h-px bg-border" />;

export const MenuLabel = ({ children }: { children: ReactNode }) => (
  <RadixMenu.Label className="px-2 py-1 text-[11px] font-semibold uppercase tracking-wide text-ink-subtle">
    {children}
  </RadixMenu.Label>
);

export const MenuCheckboxItem = ({
  checked,
  onCheckedChange,
  children,
  disabled = false,
}: {
  checked: boolean;
  onCheckedChange: (checked: boolean) => void;
  children: ReactNode;
  disabled?: boolean;
}) => (
  <RadixMenu.CheckboxItem
    checked={checked}
    disabled={disabled}
    onCheckedChange={onCheckedChange}
    // Menus of checkboxes take several picks, so they stay open.
    onSelect={(event: Event) => event.preventDefault()}
    className={cn(
      'relative flex cursor-pointer items-center gap-2 rounded-md py-1.5 pl-7 pr-2 text-[13px] text-ink outline-none',
      'data-[highlighted]:bg-sunken data-[disabled]:cursor-not-allowed data-[disabled]:opacity-50',
    )}
  >
    <RadixMenu.ItemIndicator className="absolute left-2 text-accent-fg">
      <Check className="size-3.5" />
    </RadixMenu.ItemIndicator>
    {children}
  </RadixMenu.CheckboxItem>
);

export const MenuRadioGroup = ({
  value,
  onValueChange,
  children,
}: {
  value: string;
  onValueChange: (value: string) => void;
  children: ReactNode;
}) => (
  <RadixMenu.RadioGroup value={value} onValueChange={onValueChange}>
    {children}
  </RadixMenu.RadioGroup>
);

export const MenuRadioItem = ({
  value,
  children,
  keepOpen = false,
}: {
  value: string;
  children: ReactNode;
  /** Leave the menu open after choosing — for menus that take several picks. */
  keepOpen?: boolean;
}) => (
  <RadixMenu.RadioItem
    value={value}
    {...(keepOpen ? { onSelect: (event: Event) => event.preventDefault() } : {})}
    className={cn(
      'flex cursor-pointer items-center gap-2 rounded-md py-1.5 pl-7 pr-2 text-[13px] text-ink outline-none',
      'relative data-[highlighted]:bg-sunken',
    )}
  >
    <RadixMenu.ItemIndicator className="absolute left-2 text-accent-fg">
      <Check className="size-3.5" />
    </RadixMenu.ItemIndicator>
    {children}
  </RadixMenu.RadioItem>
);
